/**
 * feedback.ts — why a person rejected an AI draft, and what the CMS may suggest because of it.
 *
 * A rejection can carry reason tags. When the same reason keeps coming up within a window, the CMS suggests ONE
 * ready-made rule for a manager to add. The suggestion is a fixed sentence per reason, counted by code: the AI is not
 * involved, nothing is added without a manager, and a rule that already exists is never suggested again.
 */
import { z } from "zod";

export const DRAFT_REJECT_TAGS = {
  off_brand_voice: { label: "Does not sound like us", rule: "Match the brand profile's voice in every sentence; if a line sounds generic or off-brand, rewrite it in plain, direct wording." },
  too_salesy: { label: "Too salesy", rule: "Keep a helpful, educational tone. Do not use hype, pressure or promotional claims; explain first and mention the product only where it genuinely helps the reader." },
  unsupported_claims: { label: "Claims without proof", rule: "Do not state a fact, figure or result unless a provided source, evidence record or knowledge passage supports it. If unsure, leave it out." },
  too_generic: { label: "Too generic", rule: "Be specific: use concrete examples, steps and details for this audience, and cut filler sentences that would fit any company." },
  wrong_audience: { label: "Wrong audience or level", rule: "Write for the stated audience and their level of knowledge. Define jargon the first time it is used and skip basics they already know." },
  poor_structure: { label: "Hard to follow", rule: "Lead with the answer, use short sections with clear headings, and keep paragraphs to a few sentences." },
  too_long: { label: "Too long", rule: "Keep drafts tight. Remove repetition and any section that does not help the reader act." },
  too_short: { label: "Too thin", rule: "Cover the topic fully: answer the main question, then the follow-up questions readers are likely to have." },
} as const;
export type DraftRejectTag = keyof typeof DRAFT_REJECT_TAGS;
export const DRAFT_REJECT_TAG_KEYS = Object.keys(DRAFT_REJECT_TAGS) as DraftRejectTag[];
export const draftRejectTagSchema = z.enum(DRAFT_REJECT_TAG_KEYS as [DraftRejectTag, ...DraftRejectTag[]]);

/** The same reason must come up this many times inside the window before a rule is suggested. */
export const FEEDBACK_SUGGESTION_RULES = { threshold: 3, windowDays: 60 } as const;

export const feedbackSuggestionSchema = z.object({
  tag: draftRejectTagSchema,
  label: z.string(),
  count: z.number().int(),
  suggestedText: z.string().max(500),
  /** Up to three short notes from the people who rejected, to show why. */
  notes: z.array(z.string()).max(3),
});
export type FeedbackSuggestion = z.infer<typeof feedbackSuggestionSchema>;
export const feedbackSuggestionsResponseSchema = z.object({
  items: z.array(feedbackSuggestionSchema),
  threshold: z.number().int(),
  windowDays: z.number().int(),
});

export interface RejectionEvent {
  tags: readonly string[];
  note: string | null;
  createdAt: string;
}

/** Reasons that reached the threshold inside the window, most frequent first, minus any whose rule already exists. */
export function suggestRulesFromFeedback(events: readonly RejectionEvent[], existingRuleTexts: readonly string[], now: Date): FeedbackSuggestion[] {
  const since = now.getTime() - FEEDBACK_SUGGESTION_RULES.windowDays * 86_400_000;
  const have = new Set(existingRuleTexts.map((t) => t.trim().toLowerCase()));
  const counts = new Map<DraftRejectTag, { count: number; notes: string[] }>();
  for (const e of events) {
    const at = Date.parse(e.createdAt);
    if (!Number.isFinite(at) || at < since || at > now.getTime() + 60_000) continue;
    for (const tag of new Set(e.tags)) {
      if (!(tag in DRAFT_REJECT_TAGS)) continue;
      const entry = counts.get(tag as DraftRejectTag) ?? { count: 0, notes: [] };
      entry.count++;
      const note = e.note?.trim();
      if (note && entry.notes.length < 3) entry.notes.push(note.slice(0, 200));
      counts.set(tag as DraftRejectTag, entry);
    }
  }
  return [...counts.entries()]
    .filter(([tag, v]) => v.count >= FEEDBACK_SUGGESTION_RULES.threshold && !have.has(DRAFT_REJECT_TAGS[tag].rule.toLowerCase()))
    .sort((a, b) => b[1].count - a[1].count || a[0].localeCompare(b[0]))
    .map(([tag, v]) => ({ tag, label: DRAFT_REJECT_TAGS[tag].label, count: v.count, suggestedText: DRAFT_REJECT_TAGS[tag].rule, notes: v.notes }));
}
